'use client';

import type { FetchBlogsQuery } from '@eliadmin/core/src/api/blogs/blogs-api';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { FetchError } from '@eliadmin/core/src/api/fetcher';
import { BLOG_GRID, BlogCard } from '@eliadmin/core/src/components/svetech/listing/blog-card';
import { EmptyState } from '@eliadmin/core/src/components/svetech/listing/empty-state';
import { ButtonLink } from '@eliadmin/core/src/components/svetech/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { toBlogCardPreview } from '@eliadmin/core/src/utils/blog-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

/** Three rows of the three-column grid. */
const PAGE_SIZE = 9;

/**
 * Paginated article cards of `/blog` and of a blog category (`?page=`, `?search=` as in core).
 * Suspends while a page loads — render it inside `<Suspense fallback={<BlogListSkeleton />}>`.
 */
export function BlogList({ locale, categoryId }: { locale: string; categoryId?: number }) {
  const { t } = useTranslations();
  const searchParams = useSearchParams();
  const authToken = useCustomerStore(s => s.me?.authToken);

  const page = Number(searchParams.get('page')) || 1;
  const search = searchParams.get('search') ?? undefined;

  const query = useMemo<FetchBlogsQuery>(
    () => ({
      where: categoryId ? { categories: { in: [categoryId] } } : undefined,
      search,
      page,
      results: PAGE_SIZE,
      orderBy: 'created_at',
      order: 'desc',
    }),
    [categoryId, page, search],
  );

  const { data } = useSuspenseQuery({
    queryKey: [CacheKey.BLOGS, locale, query, authToken ?? null, 'card-preview'],
    queryFn: async () => {
      try {
        const response = await fetchBlogs({ locale, query, authToken });
        return { ...response, data: response.data.map(toBlogCardPreview) };
      } catch (error) {
        // A store without the blog module answers 404: an empty blog, not an error page.
        if (error instanceof FetchError && error.status === 404) return { data: [], meta: null };
        throw error;
      }
    },
  });

  if (!data.data.length) {
    const back = categoryId
      ? { href: ROUTES.BLOG(), label: t('text633') }
      : { href: ROUTES.HOME() || '/', label: t('text2031') };

    return (
      <EmptyState title={t('text673')} text={t('text554')} className="py-4 md:py-8">
        <ButtonLink href={back.href} variant="text" icon="arrow-left">
          {back.label}
        </ButtonLink>
      </EmptyState>
    );
  }

  return (
    <div className="flex flex-col items-center gap-8 md:gap-10">
      <ul className={cn(BLOG_GRID, 'w-full')}>
        {data.data.map(blog => (
          <li key={blog.id}>
            <BlogCard blog={blog} locale={locale} moreLabel={t('text1360')} />
          </li>
        ))}
      </ul>
      {data.meta && data.meta.last_page > 1 ? <Pagination links={data.meta.links ?? []} /> : null}
    </div>
  );
}

export function BlogListSkeleton() {
  return (
    <ul className={BLOG_GRID} aria-hidden="true">
      {Array.from({ length: 3 }, (_, index) => (
        <li key={index} className="flex flex-col gap-3">
          <Skeleton className="aspect-[3/2] w-full rounded-3xl rounded-tr-none md:rounded-[32px] md:rounded-tr-none" />
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-16 w-full" />
        </li>
      ))}
    </ul>
  );
}
